// src/components/leaves/LeaveManagement.js
import { useState, useEffect } from 'react';
import { leavesService } from '../../services/leavesService';
import { premisesService } from '../../services/premisesService';
import { organizationsService } from '../../services/organizationsService';
import LeaveForm from './LeaveForm';
import LeaveTable from './LeaveTable';
import LeaveTypesTable from './LeaveTypesTable';
import LoadingSpinner from '../common/LoadingSpinner';
import Modal from '../common/Modal';
import { useAuth } from '../../hooks/useAuth';
import { usePageTitle } from '../../hooks/usePageTitle';

const LeaveManagement = () => {
    usePageTitle('Leave Management');
    const { user } = useAuth();
    const [leaveRequests, setLeaveRequests] = useState([]);
    const [leaveTypes, setLeaveTypes] = useState([]);
    const [premises, setPremises] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [activeTab, setActiveTab] = useState('requests');

    // Filter states - Set premises_id based on user role
    const [filters, setFilters] = useState({
        status: '',
        organization_id: user?.user_type === 'core_admin' ? '' : user?.org_id || '',
        premises_id: user?.user_type === 'app_admin' ? user?.premises_id || '' : '',
        staff_id: '',
        search: '',
        date_from: '',
        date_to: '',
        leave_type_id: '',
        page: 1,
        limit: 20
    });

    // Statistics
    const [stats, setStats] = useState({
        total: 0,
        pending: 0,
        approved: 0,
        rejected: 0
    });

    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 20,
        total: 0,
        total_pages: 1
    });

    const isAdmin = user?.user_type === 'admin' || user?.user_type === 'client_admin' || user?.user_type === 'core_admin' || user?.user_type === 'app_admin';
    const isStaff = user?.user_type === 'staff';

    useEffect(() => {
        loadData();
    }, [filters, user]);

    const loadData = async () => {
        setLoading(true);
        setError(null);
        try {
            await Promise.all([
                loadLeaveRequests(),
                loadLeaveTypes(),
                loadOrganizations(),
                loadPremises(),
                loadStats()
            ]);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const loadLeaveRequests = async () => {
        try {
            const response = await leavesService.getLeaveRequests(filters);
            // Support both API response shapes:
            // 1) { leave_requests: [...], pagination: {...} }
            // 2) { data: [...], count: N }
            const requests = response.leave_requests || response.data || [];
            setLeaveRequests(requests);

            const total = Number(
                response?.pagination?.total ??
                response?.count ??
                requests.length
            );
            const perPage = Number(response?.pagination?.per_page ?? filters.limit ?? 20);
            const currentPage = Number(response?.pagination?.current_page ?? filters.page ?? 1);
            const totalPages = Math.max(1, Number(response?.pagination?.total_pages ?? Math.ceil(total / perPage)));

            setPagination({
                current_page: currentPage,
                per_page: perPage,
                total,
                total_pages: totalPages
            });
        } catch (err) {
            console.error('Error loading leave requests:', err);
        }
    };

    const loadLeaveTypes = async () => {
        try {
            const response = await leavesService.getLeaveTypes();
            let types = response.leave_types || [];

            // Filter leave types based on user role
            // Show common types (premises_id = 0) + organization-specific types for user's premises
            if (user?.user_type === 'app_admin' && user?.premises_id) {
                types = types.filter(type =>
                    !type.premises_id ||
                    type.premises_id === 0 ||
                    type.premises_id === '0' ||
                    type.premises_id === user.premises_id ||
                    type.premises_id === String(user.premises_id)
                );
            }

            setLeaveTypes(types);
        } catch (err) {
            console.error('Error loading leave types:', err);
        }
    };

    const loadPremises = async () => {
        if (isAdmin && user?.user_type !== 'app_admin') {
            try {
                const premisesParams = {};
                if (user?.user_type === 'core_admin' && filters.organization_id) {
                    premisesParams.organization_id = filters.organization_id;
                }
                const response = await premisesService.getPremisesComplete(
                    premisesParams,
                    user?.user_type === 'core_admin'
                );
                setPremises(response.premises || []);
            } catch (err) {
                console.error('Error loading premises:', err);
            }
        }
    };

    const loadOrganizations = async () => {
        if (user?.user_type !== 'core_admin') {
            return;
        }
        try {
            const response = await organizationsService.getOrganizationsList();
            setOrganizations(Array.isArray(response) ? response : []);
        } catch (err) {
            console.error('Error loading organizations:', err);
        }
    };

    const loadStats = async () => {
        try {
            const statsParams = {
                organization_id: filters.organization_id || undefined,
                premises_id: filters.premises_id || undefined,
                status: filters.status || undefined,
                leave_type_id: filters.leave_type_id || undefined,
                date_from: filters.date_from || undefined,
                date_to: filters.date_to || undefined
            };
            if (user?.user_type === 'app_admin') {
                statsParams.premises_id = user.premises_id;
            }
            const response = await leavesService.getLeaveStats(statsParams);
            setStats(response);
        } catch (err) {
            console.error('Error loading stats:', err);
        }
    };

    const handleApplyLeave = () => {
        setShowForm(true);
    };

    const handleFormSubmit = async (formData) => {
        try {
            await leavesService.applyLeave(formData);
            await loadData();
            setShowForm(false);
            alert('Leave request submitted successfully!');
        } catch (err) {
            throw err;
        }
    };

    const handleApproveLeave = async (leaveId, status, comments = '') => {
        try {
            await leavesService.approveLeave(leaveId, status, comments);
            await loadData();
            alert(`Leave request ${status} successfully!`);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleCancelLeave = async (leaveId) => {
        if (!window.confirm('Are you sure you want to cancel this leave request?')) {
            return;
        }

        try {
            await leavesService.cancelLeave(leaveId);
            await loadData();
            alert('Leave request cancelled successfully!');
        } catch (err) {
            setError(err.message);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            page: 1
        }));
    };

    const handlePageChange = (page) => {
        setFilters(prev => ({ ...prev, page }));
    };

    const csvEscape = (value) => {
        const str = value === null || value === undefined ? '' : String(value);
        return `"${str.replace(/"/g, '""')}"`;
    };

    const handleExportCsv = () => {
        if (!leaveRequests.length) return;

        const headers = [
            'Staff Name', 'Staff ID', 'Organization', 'Premises', 'Leave Type',
            'Start Date', 'End Date', 'Total Days', 'Status', 'Reason',
            'Approver Comments', 'Requested On', 'Decided On'
        ];
        const rows = leaveRequests.map((req) => [
            req.staff_name,
            req.staff_code,
            req.organization_name,
            req.premises_name,
            req.leave_type_name,
            req.start_date,
            req.end_date,
            req.total_days,
            req.status,
            req.reason,
            req.approver_comments,
            req.created_at,
            req.approved_at
        ]);

        const csv = [headers, ...rows].map((row) => row.map(csvEscape).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const fileDate = new Date().toISOString().split('T')[0];
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `leave-requests-${fileDate}.csv`;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);
    };

    const clearFilters = () => {
        setFilters({
            status: '',
            organization_id: user?.user_type === 'core_admin' ? '' : user?.org_id || '',
            premises_id: user?.user_type === 'app_admin' ? user?.premises_id || '' : '',
            staff_id: '',
            search: '',
            date_from: '',
            date_to: '',
            leave_type_id: '',
            page: 1,
            limit: 20
        });
    };

    if (loading && leaveRequests.length === 0 && leaveTypes.length === 0) {
        return <LoadingSpinner />;
    }

    return (
        <div className="leave-management">
            <div className="container">
                {/* Header */}
                <div className="page-header">
                    <div className="header-content">
                        <div className="header-icon">
                            <i className="fas fa-umbrella-beach"></i>
                        </div>
                        <div className="header-text">
                            <h1>Leave Management</h1>
                            <p>Manage leave types and track leave requests</p>
                        </div>
                    </div>
                </div>

                {/* Leave Statistics */}
                <div className="stats-grid">
                    <div className="stat-card stat-total">
                        <div className="stat-icon">
                            <i className="fas fa-calendar-alt"></i>
                        </div>
                        <div className="stat-details">
                            <div className="stat-value">{stats.total || 0}</div>
                            <div className="stat-label">Total Requests</div>
                        </div>
                    </div>

                    <div className="stat-card stat-pending">
                        <div className="stat-icon">
                            <i className="fas fa-clock"></i>
                        </div>
                        <div className="stat-details">
                            <div className="stat-value">{stats.pending || 0}</div>
                            <div className="stat-label">Pending</div>
                        </div>
                    </div>

                    <div className="stat-card stat-approved">
                        <div className="stat-icon">
                            <i className="fas fa-check-circle"></i>
                        </div>
                        <div className="stat-details">
                            <div className="stat-value">{stats.approved || 0}</div>
                            <div className="stat-label">Approved</div>
                        </div>
                    </div>

                    <div className="stat-card stat-rejected">
                        <div className="stat-icon">
                            <i className="fas fa-times-circle"></i>
                        </div>
                        <div className="stat-details">
                            <div className="stat-value">{stats.rejected || 0}</div>
                            <div className="stat-label">Rejected</div>
                        </div>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="tab-navigation">
                    <button
                        className={`tab-btn ${activeTab === 'requests' ? 'active' : ''}`}
                        onClick={() => setActiveTab('requests')}
                    >
                        <i className="fas fa-list-alt"></i>
                        Leave Requests
                        <span className="tab-badge">{stats.pending || 0}</span>
                    </button>
                    {isAdmin && (
                        <button
                            className={`tab-btn ${activeTab === 'types' ? 'active' : ''}`}
                            onClick={() => setActiveTab('types')}
                        >
                            <i className="fas fa-tags"></i>
                            Leave Types
                            <span className="tab-badge">{leaveTypes.length}</span>
                        </button>
                    )}
                </div>

                {/* Filters Section */}
                {activeTab === 'requests' && (
                    <div className="filters-section">
                        <div className="filters-header">
                            <h3>
                                <i className="fas fa-filter"></i>
                                Filter Requests
                            </h3>
                            <div className="filters-header-actions">
                                <button
                                    className="btn btn-export btn-sm"
                                    onClick={handleExportCsv}
                                    disabled={leaveRequests.length === 0}
                                >
                                    <i className="fas fa-file-csv"></i>
                                    Export CSV
                                </button>
                                <button className="btn btn-ghost btn-sm" onClick={clearFilters}>
                                    <i className="fas fa-redo"></i>
                                    Reset
                                </button>
                            </div>
                        </div>

                        <div className="filters-grid">
                            {user?.user_type === 'core_admin' && (
                                <div className="filter-group">
                                    <label>
                                        <i className="fas fa-sitemap"></i>
                                        Organization
                                    </label>
                                    <select
                                        className="form-input"
                                        value={filters.organization_id}
                                        onChange={(e) => {
                                            handleFilterChange('organization_id', e.target.value);
                                            handleFilterChange('premises_id', '');
                                        }}
                                    >
                                        <option value="">All Organizations</option>
                                        {organizations.map(org => (
                                            <option key={org.id} value={org.id}>
                                                {org.name || org.organization_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div className="filter-group">
                                <label>
                                    <i className="fas fa-info-circle"></i>
                                    Status
                                </label>
                                <select
                                    className="form-input"
                                    value={filters.status}
                                    onChange={(e) => handleFilterChange('status', e.target.value)}
                                >
                                    <option value="">All Status</option>
                                    <option value="pending">Pending</option>
                                    <option value="approved">Approved</option>
                                    <option value="rejected">Rejected</option>
                                    <option value="cancelled">Cancelled</option>
                                </select>
                            </div>

                            {/* Only show premises filter if not app_admin */}
                            {isAdmin && user?.user_type !== 'app_admin' && (
                                <div className="filter-group">
                                    <label>
                                        <i className="fas fa-building"></i>
                                        Premises
                                    </label>
                                    <select
                                        className="form-input"
                                        value={filters.premises_id}
                                        onChange={(e) => handleFilterChange('premises_id', e.target.value)}
                                    >
                                        <option value="">All Premises</option>
                                        {premises.map(premise => (
                                            <option key={premise.id} value={premise.id}>
                                                {premise.premises_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div className="filter-group">
                                <label>
                                    <i className="fas fa-search"></i>
                                    Search
                                </label>
                                <input
                                    type="text"
                                    className="form-input"
                                    placeholder="Search by staff, reason, premises..."
                                    value={filters.search}
                                    onChange={(e) => handleFilterChange('search', e.target.value)}
                                />
                            </div>

                            <div className="filter-group">
                                <label>
                                    <i className="fas fa-tag"></i>
                                    Leave Type
                                </label>
                                <select
                                    className="form-input"
                                    value={filters.leave_type_id}
                                    onChange={(e) => handleFilterChange('leave_type_id', e.target.value)}
                                >
                                    <option value="">All Types</option>
                                    {leaveTypes.map(type => (
                                        <option key={type.id} value={type.id}>
                                            {type.leave_name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="filter-group">
                                <label>
                                    <i className="fas fa-calendar-day"></i>
                                    From Date
                                </label>
                                <input
                                    type="date"
                                    className="form-input"
                                    value={filters.date_from}
                                    onChange={(e) => handleFilterChange('date_from', e.target.value)}
                                />
                            </div>

                            <div className="filter-group">
                                <label>
                                    <i className="fas fa-calendar-day"></i>
                                    To Date
                                </label>
                                <input
                                    type="date"
                                    className="form-input"
                                    value={filters.date_to}
                                    onChange={(e) => handleFilterChange('date_to', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Error Message */}
                {error && (
                    <div className="error-message">
                        <i className="fas fa-exclamation-triangle"></i>
                        <span>{error}</span>
                    </div>
                )}

                {/* Content Tabs */}
                {activeTab === 'requests' && (
                    <LeaveTable
                        leaveRequests={leaveRequests}
                        loading={loading}
                        pagination={pagination}
                        onApprove={handleApproveLeave}
                        onCancel={handleCancelLeave}
                        onPageChange={handlePageChange}
                        isAdmin={isAdmin}
                        currentUserId={user?.id}
                        userType={user?.user_type}
                    />
                )}

                {activeTab === 'types' && isAdmin && (
                    <LeaveTypesTable
                        leaveTypes={leaveTypes}
                        loading={loading}
                        onRefresh={loadLeaveTypes}
                        userType={user?.user_type}
                        userPremisesId={user?.premises_id}
                        premises={premises}
                    />
                )}

                {/* Leave Form Modal */}
                <Modal
                    show={showForm}
                    onClose={() => setShowForm(false)}
                    title="Apply for Leave"
                    size="medium"
                >
                    <LeaveForm
                        leaveTypes={leaveTypes}
                        premises={premises}
                        onSubmit={handleFormSubmit}
                        onCancel={() => setShowForm(false)}
                        user={user}
                    />
                </Modal>
            </div>

            <style jsx>{`
        .leave-management {
          min-height: 100vh;
          background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
          padding: 2rem 0;
        }

        .container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 1.5rem;
        }

        .page-header {
          background: white;
          padding: 2rem;
          border-radius: 20px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
          margin-bottom: 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-left: 5px solid #f59e0b;
        }

        .header-content {
          display: flex;
          align-items: center;
          gap: 1.5rem;
        }

        .header-icon {
          width: 70px;
          height: 70px;
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          border-radius: 15px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2rem;
          color: white;
          box-shadow: 0 5px 15px rgba(245, 158, 11, 0.3);
        }

        .header-text h1 {
          color: #2d3748;
          margin: 0 0 0.5rem 0;
          font-size: 2rem;
          font-weight: 700;
        }

        .header-text p {
          color: #718096;
          margin: 0;
          font-size: 1rem;
        }

        .btn-gradient {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          border: none;
          padding: 0.8rem 1.5rem;
          font-size: 1rem;
          font-weight: 600;
          box-shadow: 0 5px 15px rgba(245, 158, 11, 0.3);
          transition: all 0.3s ease;
        }

        .btn-gradient:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(245, 158, 11, 0.4);
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .stat-card {
          background: white;
          padding: 1.5rem;
          border-radius: 15px;
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
          display: flex;
          align-items: center;
          gap: 1.2rem;
          transition: all 0.3s ease;
          border-left: 4px solid transparent;
        }

        .stat-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
        }

        .stat-card.stat-total {
          border-left-color: #3b82f6;
        }

        .stat-card.stat-total .stat-icon {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
        }

        .stat-card.stat-pending {
          border-left-color: #f59e0b;
        }

        .stat-card.stat-pending .stat-icon {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
        }

        .stat-card.stat-approved {
          border-left-color: #10b981;
        }

        .stat-card.stat-approved .stat-icon {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
        }

        .stat-card.stat-rejected {
          border-left-color: #ef4444;
        }

        .stat-card.stat-rejected .stat-icon {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
        }

        .stat-icon {
          width: 60px;
          height: 60px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          color: white;
          flex-shrink: 0;
        }

        .stat-details {
          flex: 1;
        }

        .stat-value {
          font-size: 2rem;
          font-weight: 700;
          color: #2d3748;
          line-height: 1;
          margin-bottom: 0.3rem;
        }

        .stat-label {
          font-size: 0.9rem;
          color: #718096;
          font-weight: 500;
        }

        .tab-navigation {
          background: white;
          border-radius: 15px;
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
          margin-bottom: 2rem;
          display: flex;
          padding: 0.5rem;
          gap: 0.5rem;
        }

        .tab-btn {
          flex: 1;
          padding: 1rem 1.5rem;
          border: none;
          background: transparent;
          color: #718096;
          border-radius: 12px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.7rem;
          font-size: 0.95rem;
          font-weight: 600;
          transition: all 0.3s ease;
          position: relative;
        }

        .tab-btn:hover:not(.active) {
          background: rgba(245, 158, 11, 0.1);
          color: #f59e0b;
        }

        .tab-btn.active {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          color: white;
          box-shadow: 0 5px 15px rgba(245, 158, 11, 0.3);
        }

        .tab-badge {
          background: rgba(255, 255, 255, 0.3);
          padding: 0.2rem 0.6rem;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 700;
        }

        .tab-btn.active .tab-badge {
          background: rgba(255, 255, 255, 0.3);
        }

        .tab-btn:not(.active) .tab-badge {
          background: rgba(245, 158, 11, 0.2);
          color: #f59e0b;
        }

        .filters-section {
          background: white;
          padding: 2rem;
          border-radius: 20px;
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
          margin-bottom: 2rem;
        }

        .filters-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 2px solid #e2e8f0;
        }

        .filters-header h3 {
          font-size: 1.3rem;
          font-weight: 600;
          color: #2d3748;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.7rem;
        }

        .filters-header h3 i {
          color: #f59e0b;
        }

        .btn-ghost {
          background: transparent;
          border: 2px solid #e2e8f0;
          color: #718096;
          transition: all 0.3s ease;
        }

        .btn-ghost:hover {
          border-color: #f59e0b;
          color: #f59e0b;
          background: rgba(245, 158, 11, 0.05);
        }

        .filters-header-actions {
          display: flex;
          gap: 0.6rem;
          flex-wrap: wrap;
        }

        .btn-export {
          background: #065f46;
          border: 2px solid #065f46;
          color: #fff;
          transition: all 0.3s ease;
        }

        .btn-export:hover {
          background: #047857;
          border-color: #047857;
        }

        .btn-export:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .filters-grid {
          display: flex;
          gap: 1.5rem;
          align-items: flex-end;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 0.6rem;
          flex: 1;
          min-width: 0;
        }

        .filter-group label {
          font-weight: 600;
          color: #2d3748;
          font-size: 0.9rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .filter-group label i {
          color: #f59e0b;
          font-size: 0.85rem;
        }

        .form-input {
          padding: 0.75rem 1rem;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          font-size: 0.95rem;
          transition: all 0.3s ease;
          background: white;
        }

        .form-input:focus {
          outline: none;
          border-color: #f59e0b;
          box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
        }

        .error-message {
          background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
          color: #dc2626;
          padding: 1rem 1.5rem;
          border-radius: 12px;
          margin-bottom: 2rem;
          display: flex;
          align-items: center;
          gap: 0.8rem;
          border-left: 4px solid #dc2626;
          font-weight: 500;
        }

        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
            gap: 1.5rem;
            text-align: center;
          }

          .header-content {
            flex-direction: column;
          }

          .stats-grid {
            grid-template-columns: 1fr;
          }

          .tab-navigation {
            flex-direction: column;
            gap: 0.5rem;
          }

          .filters-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
        </div>
    );
};

export default LeaveManagement;
